// Web shim for lottie-react-native
// 在 Web 环境下使用 lottie-web 替代

import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { View, StyleSheet, Platform } from 'react-native';

// 在 web 上使用原生 div
const WebDiv = Platform.OS === 'web' ? 'div' : View;

// 动态导入 lottie-web（仅在 web 平台）
let lottieWeb: any = null;
if (Platform.OS === 'web') {
  try {
    // 直接 require lottie-web
    const lottieWebModule = require('lottie-web');
    // lottie-web 可能是 default export 或者直接导出
    lottieWeb = lottieWebModule.default || lottieWebModule;
    
    if (!lottieWeb || typeof lottieWeb.loadAnimation !== 'function') {
      console.error('lottie-web loaded but loadAnimation not found', {
        hasLottieWeb: !!lottieWeb,
        type: typeof lottieWeb,
        keys: lottieWeb ? Object.keys(lottieWeb) : [],
      });
      lottieWeb = null;
    } else {
      console.log('lottie-web loaded successfully');
    }
  } catch (e) {
    console.error('Failed to load lottie-web:', e);
    lottieWeb = null;
  }
}

export interface LottieViewProps {
  source: any;
  style?: any;
  autoPlay?: boolean;
  loop?: boolean;
  speed?: number;
  imageAssetsFolder?: string;
  resizeMode?: 'contain' | 'cover' | 'center';
  onAnimationFinish?: () => void;
}

export interface LottieViewRef {
  play: () => void;
  pause: () => void;
  reset: () => void;
  resume: () => void;
}

const LottieViewWeb = forwardRef<LottieViewRef, LottieViewProps>((props, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<any>(null);
  const { source, style, autoPlay = false, loop = false, speed = 1, imageAssetsFolder = 'lottie/images', resizeMode = 'contain' } = props;

  useImperativeHandle(ref, () => ({
    play: () => {
      if (animationRef.current) {
        animationRef.current.play();
      }
    },
    pause: () => {
      if (animationRef.current) {
        animationRef.current.pause();
      }
    },
    reset: () => {
      if (animationRef.current) {
        animationRef.current.goToAndStop(0, true);
      }
    },
    resume: () => {
      if (animationRef.current) {
        animationRef.current.play();
      }
    },
  }));

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    if (!lottieWeb) {
      console.warn('LottieView: lottie-web not available');
      return;
    }

    if (!containerRef.current) {
      return;
    }

    // 获取 JSON 数据
    let animationData: any;
    // webpack 的 require() 在 web 上返回的是模块对象，需要获取 default 或直接使用
    if (source && typeof source === 'object') {
      if (source.default) {
        animationData = source.default;
      } else if (source.__esModule && source.default) {
        animationData = source.default;
      } else {
        animationData = source;
      }
    } else {
      animationData = source;
    }

    if (!animationData) {
      console.warn('LottieView: No animation data found', source);
      return;
    }

    // 确保 animationData 是有效的 Lottie JSON 对象
    if (typeof animationData !== 'object' || !animationData.v) {
      console.warn('LottieView: Invalid animation data format', { 
        hasV: !!animationData?.v, 
        type: typeof animationData,
        keys: animationData ? Object.keys(animationData) : []
      });
      return;
    }

    // 处理图片资源路径
    // 在 web 上，lottie-web 需要图片的完整 URL
    // webpack 会将图片输出到 /lottie/images/ 目录
    // 如果 animationData 中有 assets，需要处理图片路径
    if (animationData && animationData.assets && Array.isArray(animationData.assets)) {
      const processedAssets = animationData.assets.map((asset: any) => {
        if (asset.p && asset.u) {
          // 构建图片的完整路径
          // asset.u 通常是 "images/"，asset.p 是文件名如 "voice_img_0.png"
          // 在 web 上，直接使用 publicPath
          const imageFileName = asset.p;
          // 使用 publicPath，webpack dev server 会处理这个路径
          const imageUrl = `/lottie/images/${imageFileName}`;
          
          return {
            ...asset,
            p: imageUrl,
            u: '', // 清空 u，使用完整路径
          };
        }
        return asset;
      });
      
      animationData = {
        ...animationData,
        assets: processedAssets,
      };
    }

    // 创建 Lottie 动画
    try {
      if (!lottieWeb || typeof lottieWeb.loadAnimation !== 'function') {
        console.error('LottieView: lottieWeb.loadAnimation is not a function');
        return;
      }

      animationRef.current = lottieWeb.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop: loop,
        autoplay: autoPlay,
        animationData: animationData,
        rendererSettings: {
          preserveAspectRatio: resizeMode === 'contain' ? 'xMidYMid meet' : 
                              resizeMode === 'cover' ? 'xMidYMid slice' : 'xMidYMid meet',
        },
      });

      // 设置播放速度
      if (speed !== 1 && animationRef.current) {
        animationRef.current.setSpeed(speed);
      }
    } catch (error) {
      console.error('LottieView: Failed to load animation', error, {
        errorMessage: error instanceof Error ? error.message : String(error),
        animationData: animationData,
        lottieWeb: lottieWeb,
      });
    }

    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [source, autoPlay, loop, speed, imageAssetsFolder, resizeMode]);

  if (Platform.OS !== 'web') {
    // 非 web 平台，返回空组件（应该使用原生组件）
    return <View style={style} />;
  }

  if (!lottieWeb) {
    // lottie-web 未安装，返回占位符
    return <View style={style} />;
  }

  const containerStyle: any = {
    width: style?.width || '100%',
    height: style?.height || '100%',
    ...(Platform.OS === 'web' && {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
  };

  if (Platform.OS === 'web') {
    // 在 web 上使用 React.createElement 创建原生 div
    return React.createElement('div', {
      ref: containerRef,
      style: containerStyle,
    });
  }

  return <View style={style} />;
});

LottieViewWeb.displayName = 'LottieView';

export default LottieViewWeb;

